// @givmo/board — the Governance page's Documents part: the governing documents, each as a chain of
// the versions the board adopted (D-CBG-9 made the former page one of the Governance page's two
// parts).
//
// ══ WHAT THIS SCREEN IS RESPONSIBLE FOR GETTING RIGHT ═══════════════════════════════════════════
//
//   * THE VERSION IN FORCE LEADS, AND THE HISTORY IS A CHAIN BELOW IT. That is the shape of the thing:
//     one document, several versions, one of them standing. The chain is rendered in the order the
//     backend WALKED (pointer by pointer), never re-sorted here — a back-dated version must not be
//     able to reorder a board's history.
//
//   * NOTHING IS NUMBERED. A reader for whom the first two versions are withheld would see the third
//     as "Version 1", so versions are identified by what they say and when they were adopted. See
//     `govdocs-decode.ts` for the full reasoning.
//
//   * THERE IS NO ENTRY FOR AN INSTRUMENT THE ORGANIZATION HAS NOT RECORDED. The registry lists what the
//     organization HAS, and the backend deliberately returns nothing for what it has not. A list of
//     instruments an organization is missing would be the product telling a board what it ought to have —
//     the plan's Band-3 line — so this page has no such list, no completeness meter, and no empty
//     row per unrecorded policy. The kind picker in the dialog is where the full set appears, as a
//     CHOICE.
//
//   * A DRAFT IS NOT A VERSION, AND IT IS ABSENT FROM THE CHAIN. The registry read excludes drafts
//     deliberately — including one could make "the version in force" name something nobody approved.
//     But an officer who saved a draft here and then could not find it would reasonably conclude the
//     product lost it, so the drafts are shown in their OWN group, "Not yet approved". They become
//     versions on approval, which happens on the record itself.
//
// ══ TWO BUTTONS, AND THE IMPORT CHOICE INSIDE THEM (CBG-UX1, Jonathan's approved mockup m5) ═════
//
// "Add a document" is the part's one action; "Add a version" is each document's. Bringing in a
// version adopted before the board used Givmo is a CHOICE inside both — the "Add a version" menu, and
// the draft-or-import choice at the top of "Add a document"'s dialog — so the part has no separate
// import button. Each act is offered only to a holder of its own string (drafting is
// `board_record:write`, importing `board_record:import`), and a menu of one is a plain button. The
// version in force has no outline or fill: its plain "In force" says it.

import { useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '../../board-routing';
import {
  type OrgBoardGoverningDocumentOut,
  type OrgBoardGoverningDocumentVersionOut,
  type OrgBoardRecordOut,
} from '@givmo/api-client';
import { Button, Card, CardBody, EmptyState, Icon, Menu, Skeleton } from '@givmo/design-system';
import { ChevronDown, Scale } from 'lucide-react';
import { GovernancePartHeader } from '../governance/governance-part-header';
import { GroupLabel, HowThisWorks, StatusWord } from '../../components/fine-print';
// CBG-UX12: the note an AI wrote on what was its proposal, behind its one link.
import { AiRationale } from '../proposals/ai-rationale';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { extractRecords, recordStateLabel } from '../records/records-decode';
import { VersionDialog, type VersionDialogMode } from './version-dialog';
import {
  adoptedOnLine,
  adoptingRecordId,
  artifactRef,
  chainVersions,
  documentTitle,
  extractGoverningDocuments,
  isInForce,
  versionLineage,
} from './govdocs-decode';
import { useBoardOperations } from '../../board-runtime';

export function governingDocumentsQueryKey(orgRef: string | null): readonly unknown[] {
  return ['board-governing-documents', orgRef];
}

/** What the dialog was opened for, and against which instrument. */
interface DialogTarget {
  mode: VersionDialogMode;
  /** The modes the dialog may offer as a choice ("Add a document" with both strings held). */
  modes: readonly VersionDialogMode[];
  kind?: string;
  label?: string;
}

/** The fine print behind the part's one line — how versions work, never what a board should adopt. */
const HOW_DOCUMENTS_WORK = [
  'The version in force leads each document; the versions it replaced follow.',
  'A new version starts as a draft in your minute book and joins its document once an officer approves it.',
  'Versions from before your board used Givmo can be imported as they are.',
  'Givmo doesn’t interpret what your documents say.',
] as const;

/** The Documents part of the Governance page. */
export function GoverningDocumentsPart() {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { permissions, orgRef, isReady } = useBoardHost();
  const canImport = hasBoardPermission(permissions, 'board_record:import');
  // Recording a version drafts it — the backend's `board_record:write`.
  const canDraft = hasBoardPermission(permissions, 'board_record:write');
  const [dialog, setDialog] = useState<DialogTarget | null>(null);
  // The acts this person may take, drafting first: the order the choice is offered in.
  const heldModes: VersionDialogMode[] = [
    ...(canDraft ? (['draft'] as const) : []),
    ...(canImport ? (['import'] as const) : []),
  ];

  const registryQuery = useQuery({
    queryKey: governingDocumentsQueryKey(orgRef),
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardGoverningDocumentOut[]> => {
      const { data } = await operations.officerListBoardGoverningDocuments({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractGoverningDocuments(data);
    },
  });

  // The versions still being worked on. A separate read because the registry EXCLUDES them by design
  // — a draft is not a version — and there is no other surface on which an officer would find the
  // draft they just saved.
  const draftsQuery = useQuery({
    queryKey: ['board-records', orgRef, 'governing-document-drafts'],
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardRecordOut[]> => {
      const { data } = await operations.officerListBoardRecords({
        path: { org_ref: orgRef ?? '' },
        query: { record_kind: 'governing_document', 'page[limit]': 100 },
        throwOnError: true,
      });
      return extractRecords(data).filter(
        (record) => record.state === 'draft' || record.state === 'submitted',
      );
    },
  });

  const documents = registryQuery.data ?? [];
  const drafts = draftsQuery.data ?? [];

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: governingDocumentsQueryKey(orgRef),
    });
    await queryClient.invalidateQueries({
      queryKey: ['board-records', orgRef, 'governing-document-drafts'],
    });
  };

  const addDocument = () => {
    const first = heldModes[0];
    if (first !== undefined) setDialog({ mode: first, modes: heldModes });
  };

  return (
    <div className="flex flex-col gap-20" data-testid="governance-documents">
      <GovernancePartHeader
        description={
          <>
            Your bylaws and policies, and each version your board adopted.{' '}
            <HowThisWorks testId="govdocs-how" lines={HOW_DOCUMENTS_WORK} />
          </>
        }
        actions={
          heldModes.length > 0 ? (
            <Button
              appearance="saas"
              tone="primary"
              disabled={!isReady || orgRef === null}
              onClick={addDocument}
              testId="govdocs-record"
            >
              Add a document
            </Button>
          ) : undefined
        }
      />

      {registryQuery.isPending && isReady ? (
        <Skeleton className="h-84 w-full rounded-lg" testId="govdocs-loading" />
      ) : null}

      {registryQuery.isError ? (
        <Card testId="govdocs-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this board’s governing documents.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void registryQuery.refetch()}
              testId="govdocs-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {registryQuery.isSuccess && documents.length === 0 ? (
        <EmptyState
          testId="govdocs-empty"
          icon={<Icon icon={Scale} size={20} aria-hidden />}
          title="No governing documents yet"
          description={`Your bylaws and policies live here, each as the versions your board adopted.${emptyInvitation(canDraft, canImport)}`}
          action={
            heldModes.length > 0 ? (
              <Button
                appearance="saas"
                tone="primary"
                onClick={addDocument}
                testId="govdocs-empty-record"
              >
                Add a document
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {documents.map((document) => (
        <DocumentCard
          key={document.chain_key}
          document={document}
          canDraft={canDraft}
          canImport={canImport}
          onAddVersion={(mode) =>
            setDialog({
              mode,
              modes: [mode],
              kind: document.document_kind,
              ...(document.label ? { label: document.label } : {}),
            })
          }
        />
      ))}

      {drafts.length > 0 ? (
        <section className="flex flex-col gap-10" data-testid="govdocs-drafts">
          <GroupLabel>Not yet approved</GroupLabel>
          <Card className="overflow-hidden">
            <ul className="divide-y divide-border-subtle">
              {drafts.map((draft) => (
                <li
                  key={draft.id}
                  className="flex items-center justify-between gap-12 px-20 py-12"
                  data-testid={`govdocs-draft-${draft.id}`}
                >
                  <Link
                    to="/records/$recordId"
                    params={{ recordId: draft.id }}
                    className="min-w-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                    data-testid={`govdocs-draft-link-${draft.id}`}
                  >
                    {draft.title}
                  </Link>
                  <StatusWord tone={draft.state === 'submitted' ? 'act' : 'plain'}>
                    {recordStateLabel(draft.state)}
                  </StatusWord>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      {dialog !== null && orgRef !== null ? (
        <VersionDialog
          orgRef={orgRef}
          mode={dialog.mode}
          modes={dialog.modes}
          documents={documents}
          {...(dialog.kind !== undefined ? { initialKind: dialog.kind } : {})}
          {...(dialog.label !== undefined ? { initialLabel: dialog.label } : {})}
          onClose={() => setDialog(null)}
          onSaved={(record) => {
            setDialog(null);
            void refresh();
            // Both doors land on the record itself. An imported version IS the record on entry; a
            // draft's page is where it is submitted and approved — and the registry shows neither of
            // them the way the officer just left it, so a list would be the wrong destination.
            void navigate({ to: '/records/$recordId', params: { recordId: record.id } });
          }}
        />
      ) : null}
    </div>
  );
}

function DocumentCard({
  document,
  canDraft,
  canImport,
  onAddVersion,
}: {
  document: OrgBoardGoverningDocumentOut;
  canDraft: boolean;
  canImport: boolean;
  onAddVersion: (mode: VersionDialogMode) => void;
}) {
  const versions = chainVersions(document);

  return (
    <Card className="overflow-hidden" testId={`govdoc-${document.chain_key}`}>
      <div className="flex items-center justify-between gap-12 border-b border-border-subtle px-20 py-14">
        <h3 className="min-w-0 font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          {documentTitle(document)}
        </h3>
        {canDraft && canImport ? (
          <Menu.Root>
            <Menu.Trigger
              render={
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  trailingIcon={<Icon icon={ChevronDown} size={14} aria-hidden />}
                  testId={`govdoc-add-version-${document.chain_key}`}
                />
              }
            >
              Add a version
            </Menu.Trigger>
            <Menu.Content
              saas
              align="end"
              className="min-w-[280px]"
              testId={`govdoc-add-version-menu-${document.chain_key}`}
            >
              <Menu.Item
                saas
                onClick={() => onAddVersion('draft')}
                testId={`govdoc-record-version-${document.chain_key}`}
              >
                <span className="flex flex-col gap-2">
                  <span>Draft a new version</span>
                  <span className="text-s12 font-w400 text-text-tertiary">
                    It joins the document once approved
                  </span>
                </span>
              </Menu.Item>
              <Menu.Item
                saas
                onClick={() => onAddVersion('import')}
                testId={`govdoc-import-version-${document.chain_key}`}
              >
                <span className="flex flex-col gap-2">
                  <span>Import an earlier version</span>
                  <span className="text-s12 font-w400 text-text-tertiary">
                    One adopted before your board used Givmo
                  </span>
                </span>
              </Menu.Item>
            </Menu.Content>
          </Menu.Root>
        ) : canDraft || canImport ? (
          // A menu of one is a plain button that does the one thing.
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => onAddVersion(canDraft ? 'draft' : 'import')}
            testId={
              canDraft
                ? `govdoc-record-version-${document.chain_key}`
                : `govdoc-import-version-${document.chain_key}`
            }
          >
            {canDraft ? 'Add a version' : 'Import an earlier version'}
          </Button>
        ) : null}
      </div>

      <ul className="divide-y divide-border-subtle">
        {versions.map((version, index) => (
          <VersionRow
            key={version.record_id}
            version={version}
            versions={versions}
            chainKey={document.chain_key}
            index={index}
          />
        ))}
      </ul>
    </Card>
  );
}

function VersionRow({
  version,
  versions,
  chainKey,
  index,
}: {
  version: OrgBoardGoverningDocumentVersionOut;
  versions: readonly OrgBoardGoverningDocumentVersionOut[];
  chainKey: string;
  index: number;
}) {
  const lineage = versionLineage(versions, version);
  const adoptingId = adoptingRecordId(version);
  const reference = artifactRef(version);
  const inForce = isInForce(version);
  const imported = version.record?.provenance === 'imported';
  const source = (version.record?.imported_source_note ?? '').trim();
  // CBG-UX12: a version an AI proposed carries the AI's own note, which waits behind "Read the AI
  // assistant's rationale" as it does on the record's page; the facts line then says "Imported"
  // alone. A person's own note keeps its place in the line (review-318's F3 and P4).
  const aiNote = imported && version.record?.import_origin === 'connected_ai' ? source : '';
  // The version's title describes its rationale toggle (CBG-FIX170): every version's reads the same.
  const titleId = useId();

  return (
    <li
      className="grid grid-cols-1 items-start gap-x-16 gap-y-2 px-20 py-12 sm:grid-cols-[minmax(0,1fr)_auto]"
      data-testid={`govdoc-version-${chainKey}-${index}`}
    >
      <Link
        to="/records/$recordId"
        params={{ recordId: version.record_id }}
        id={titleId}
        className="min-w-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
        data-testid={`govdoc-version-link-${version.record_id}`}
      >
        {(version.record?.title ?? '').trim() || 'This version'}
      </Link>
      {/* From `sm`, "In force" is the row's right-hand column; on a phone it leads the facts line. */}
      <span className="hidden self-center sm:row-span-2 sm:block">
        {inForce ? <StatusWord testId={`govdoc-in-force-${chainKey}`}>In force</StatusWord> : null}
      </span>
      <span
        className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
        data-testid={`govdoc-version-facts-${version.record_id}`}
      >
        {inForce ? (
          <span className="sm:hidden">
            <StatusWord>In force</StatusWord>
            {' · '}
          </span>
        ) : null}
        {adoptedOnLine(version)}
        {imported
          ? ` · Imported${source.length > 0 && aiNote === '' ? ` from ${source}` : ''}`
          : ''}
      </span>
      {aiNote.length > 0 ? (
        <div className="min-w-0 sm:col-span-2">
          <AiRationale
            note={aiNote}
            testId={`govdoc-version-note-${version.record_id}`}
            describedBy={titleId}
          />
        </div>
      ) : null}
      {/* VERSION lineage, in replacement words. A correction is a different act and is never
          rendered here — corrections are excluded from the chain entirely, and the minute book's
          record page is where correction lineage appears, under its own heading. */}
      {lineage !== null ? (
        <span
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary sm:col-span-2"
          data-testid={`govdoc-version-lineage-${version.record_id}`}
        >
          {lineage.line}
        </span>
      ) : null}
      {adoptingId !== null ? (
        <Link
          to="/records/$recordId"
          params={{ recordId: adoptingId }}
          className="font-sans text-s12 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline sm:col-span-2"
          data-testid={`govdoc-version-adopted-by-${version.record_id}`}
        >
          The record of the act that adopted it
        </Link>
      ) : null}
      {reference !== null ? (
        <span
          className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary sm:col-span-2"
          data-testid={`govdoc-version-artifact-${version.record_id}`}
        >
          Your copy: <code className="select-all font-mono">{reference}</code>
        </span>
      ) : null}
    </li>
  );
}

/** The empty registry's invitation: only the acts the person can take (drafting, importing). */
function emptyInvitation(canDraft: boolean, canImport: boolean): string {
  if (canDraft && canImport) {
    return ' Add the one in force, or import the versions your board adopted before it used Givmo.';
  }
  if (canDraft) return ' Add the one in force.';
  if (canImport) return ' Import the versions your board adopted before it used Givmo.';
  return '';
}
